import { IGame } from '@/api/games';
import { accessTokenAtom } from '@/stores/auth';
import { openConnectAtom } from '@/stores/layout';
import { formatLamports } from '@/utils/number';
import { Ufc } from '@/utils/program/idl/type';
import { getInstantGameAddr, getVaultAddr } from '@/utils/program/pda';
import { Program } from '@coral-xyz/anchor';
import { PublicKey } from '@solana/web3.js';
import BN from 'bn.js';
import { atom } from 'jotai';
import _ from 'lodash';

export const _programAtom = atom<Program<Ufc> | null>(null);

export const programAtom = atom(
  (get) => get(_programAtom),
  (get, set, val: Program<Ufc> | null) => {
    set(_programAtom, val);

    if (val) {
      set(_waitForLoggedAtom, (prev) => {
        prev.forEach((fn) => fn.resolve(val));
        return [];
      });
    } else {
      set(accessTokenAtom, null);
    }
  }
);

export const _waitForLoggedAtom = atom<
  Array<{
    resolve: (val: Program<Ufc>) => any;
    reject: (reason: any) => any;
  }>
>([]);

export const waitForLoggedAtom = atom(
  null,
  async (get, set, opts?: { rejectAll?: boolean }) => {
    if (opts?.rejectAll) {
      set(_waitForLoggedAtom, (prev) => {
        prev.forEach((fn) => fn.reject('REJECT_ALL'));
        return [];
      });
      throw 'REJECT_ALL';
    }
    const program = get(_programAtom);
    if (program) return Promise.resolve(program);

    set(openConnectAtom, true);

    return await new Promise<Program<Ufc>>((resolve, reject) => {
      set(_waitForLoggedAtom, (prev) => {
        // prev.forEach(fn => fn.reject('REJECT_PREV'))
        return [{ resolve, reject }];
      });
    });
  }
);

export interface BuyTicketPayload {
  stakes: { [frogId: string]: string | number };
  game: IGame;
}

export const buyTicketAtom = atom(
  null,
  async (get, set, data: BuyTicketPayload) => {
    const program = await set(waitForLoggedAtom);

    const payload = Object.entries(data.stakes).flatMap(([frogId, stake]) => {
      if (+stake <= 0) return [];

      return {
        id: +frogId,
        stake: new BN(formatLamports(stake)),
      };
    });

    if (_.isEmpty(payload)) return;

    const accounts = {
      buyer: program.provider.publicKey,
      vault: getVaultAddr(program),
      game: new PublicKey(data.game.pdaAddress),
    } as any;

    return await program.methods.placeBet(payload).accounts(accounts).rpc();
  }
);

export interface BuyInstantTicketPayload {
  stakes: { [outcomeId: string]: string | number };
  gameId: number;
  roundId: number;
}

export const buyInstantTicketAtom = atom(
  null,
  async (get, set, data: BuyInstantTicketPayload) => {
    const program = await set(waitForLoggedAtom);

    const payload = Object.entries(data.stakes).flatMap(([frogId, stake]) => {
      if (+stake <= 0) return [];

      return {
        id: +frogId,
        stake: new BN(formatLamports(stake)),
      };
    });

    if (_.isEmpty(payload)) return;

    const accounts = {
      buyer: program.provider.publicKey,
      vault: getVaultAddr(program),
      game: getInstantGameAddr({ program, gameId: data.gameId }),
    } as any;

    const txhash = await program.methods
      .instantPlaceBet(new BN(data.roundId), payload)
      .accounts(accounts)
      .rpc();

    return txhash;
  }
);
